import React, { useState } from 'react'
import { cn } from './utils/cn'
import { ChevronDownIcon, ChevronRightIcon, MapPinIcon, CheckIcon } from '@heroicons/react/24/outline'

// Malaysian states and cities data
export const MALAYSIAN_STATES = [
  {
    id: 'johor',
    name: 'Johor',
    cities: [
      'Johor Bahru', 'Batu Pahat', 'Kluang', 'Muar', 'Segamat',
      'Kulai', 'Pontian', 'Kota Tinggi', 'Mersing', 'Tangkak'
    ]
  },
  {
    id: 'kedah',
    name: 'Kedah',
    cities: [
      'Alor Setar', 'Sungai Petani', 'Kulim', 'Langkawi', 'Kuala Kedah',
      'Yan', 'Sik', 'Baling', 'Padang Terap', 'Pokok Sena'
    ]
  },
  {
    id: 'kelantan',
    name: 'Kelantan',
    cities: [
      'Kota Bharu', 'Pasir Mas', 'Tumpat', 'Bachok', 'Kuala Krai',
      'Machang', 'Tanah Merah', 'Pasir Puteh', 'Gua Musang', 'Jeli'
    ]
  },
  {
    id: 'kuala-lumpur',
    name: 'Kuala Lumpur',
    cities: [
      'Kuala Lumpur City', 'Bangsar', 'Petaling Jaya', 'Subang Jaya',
      'Ampang', 'Cheras', 'Kepong', 'Selayang', 'Gombak', 'Wangsa Maju'
    ]
  },
  {
    id: 'labuan',
    name: 'Labuan',
    cities: [
      'Victoria (Labuan)', 'Batu Manikar', 'Layang-Layang', 'Rampayan',
      'Sang Kancil', 'Pohon Batu'
    ]
  },
  {
    id: 'melaka',
    name: 'Melaka',
    cities: [
      'Melaka City', 'Alor Gajah', 'Jasin', 'Masjid Tanah', 'Merlimau',
      'Tampin', 'Sungai Udang', 'Batu Berendam', 'Bukit Beruang'
    ]
  },
  {
    id: 'negeri-sembilan',
    name: 'Negeri Sembilan',
    cities: [
      'Seremban', 'Port Dickson', 'Nilai', 'Bahau', 'Kuala Pilah',
      'Rembau', 'Tampin', 'Jempol', 'Gemenceh'
    ]
  },
  {
    id: 'pahang',
    name: 'Pahang',
    cities: [
      'Kuantan', 'Temerloh', 'Bentong', 'Jerantut', 'Mentakab',
      'Raub', 'Lipis', 'Maran', 'Pekan', 'Bera'
    ]
  },
  {
    id: 'penang',
    name: 'Penang',
    cities: [
      'George Town', 'Butterworth', 'Bukit Mertajam', 'Seberang Perai',
      'Balik Pulau', 'Nibong Tebal', 'Tanjung Bungah', 'Bayan Lepas'
    ]
  },
  {
    id: 'perak',
    name: 'Perak',
    cities: [
      'Ipoh', 'Taiping', 'Teluk Intan', 'Sitiawan', 'Kampar',
      'Kuala Kangsar', 'Parit Buntar', 'Batu Gajah', 'Tanjung Malim'
    ]
  },
  {
    id: 'perlis',
    name: 'Perlis',
    cities: [
      'Kangar', 'Arau', 'Kuala Perlis', 'Padang Besar', 'Simpang Empat'
    ]
  },
  {
    id: 'putrajaya',
    name: 'Putrajaya',
    cities: [
      'Putrajaya City', 'Precinct 1', 'Precinct 8', 'Precinct 9',
      'Precinct 11', 'Precinct 12', 'Precinct 13', 'Precinct 16', 'Precinct 18'
    ]
  },
  {
    id: 'sabah',
    name: 'Sabah',
    cities: [
      'Kota Kinabalu', 'Sandakan', 'Tawau', 'Lahad Datu', 'Keningau',
      'Kota Belud', 'Kinarut', 'Beaufort', 'Kunak', 'Papart'
    ]
  },
  {
    id: 'sarawak',
    name: 'Sarawak',
    cities: [
      'Kuching', 'Miri', 'Sibu', 'Bintulu', 'Sarikei',
      'Kapit', 'Limbang', 'Sri Aman', 'Betong', 'Mukah'
    ]
  },
  {
    id: 'selangor',
    name: 'Selangor',
    cities: [
      'Shah Alam', 'Petaling Jaya', 'Subang Jaya', 'Klang', 'Kajang',
      'Bangi', 'Rawang', 'Ampang', 'Hulu Selangor', 'Sepang'
    ]
  },
  {
    id: 'terengganu',
    name: 'Terengganu',
    cities: [
      'Kuala Terengganu', 'Kemaman', 'Dungun', 'Marang', 'Besut',
      'Hulu Terengganu', 'Kuala Nerus', 'Setiu'
    ]
  }
] as const

export interface MalaysianState {
  id: string
  name: string
  cities: readonly string[]
}

export interface LocationSelection {
  states: string[]
  cities: string[]
}

export interface LocationSelectorProps {
  value?: LocationSelection
  onChange?: (selection: LocationSelection) => void
  className?: string
  label?: string
  error?: string
  helperText?: string
  required?: boolean
  disabled?: boolean
  showCities?: boolean
  maxSelections?: number
}

const LocationSelector = React.forwardRef<HTMLDivElement, LocationSelectorProps>(
  ({ 
    value = { states: [], cities: [] }, 
    onChange, 
    className, 
    label, 
    error, 
    helperText, 
    required = false,
    disabled = false,
    showCities = true,
    maxSelections,
    ...props 
  }, ref) => {
    const [expandedStates, setExpandedStates] = useState<Set<string>>(new Set())
    const [searchTerm, setSearchTerm] = useState('')

    const toggleState = (stateId: string) => {
      if (disabled) return

      const isSelected = value.states.includes(stateId)
      let newStates: string[]
      let newCities: string[] = [...value.cities]

      if (isSelected) {
        // Remove state and its cities
        newStates = value.states.filter(s => s !== stateId)
        const stateCities = MALAYSIAN_STATES.find(s => s.id === stateId)?.cities || []
        newCities = newCities.filter(city => !stateCities.includes(city))
      } else {
        // Check max selections
        if (maxSelections && value.states.length >= maxSelections) {
          return
        }
        // Add state
        newStates = [...value.states, stateId]
        // Auto-expand the newly selected state
        setExpandedStates(prev => new Set([...prev, stateId]))
      }

      onChange?.({
        states: newStates,
        cities: newCities
      })
    }

    const toggleCity = (city: string, stateId: string) => {
      if (disabled) return

      const isStateSelected = value.states.includes(stateId)
      const isSelected = value.cities.includes(city)
      let newStates = [...value.states]
      let newCities: string[]

      if (isSelected) {
        // Remove city
        newCities = value.cities.filter(c => c !== city)
      } else {
        // Add city and ensure state is selected
        newCities = [...value.cities, city]
        if (!isStateSelected) {
          newStates = [...newStates, stateId]
        }
      }

      onChange?.({
        states: newStates,
        cities: newCities
      })
    }

    const toggleStateExpansion = (stateId: string) => {
      setExpandedStates(prev => {
        const newSet = new Set(prev)
        if (newSet.has(stateId)) {
          newSet.delete(stateId)
        } else {
          newSet.add(stateId)
        }
        return newSet
      })
    }

    const filteredStates = MALAYSIAN_STATES.filter(state => 
      state.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      state.cities.some(city => city.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    const isStateFullySelected = (state: MalaysianState) => {
      return state.cities.every(city => value.cities.includes(city))
    }

    const isStatePartiallySelected = (state: MalaysianState) => {
      const selectedCitiesInState = state.cities.filter(city => value.cities.includes(city))
      return selectedCitiesInState.length > 0 && selectedCitiesInState.length < state.cities.length
    }

    const selectedStateCount = value.states.length
    const selectedCityCount = value.cities.length

    return (
      <div className="space-y-2" ref={ref} {...props}>
        {label && (
          <label className="text-sm font-medium text-neutral-700 flex items-center">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
            <MapPinIcon className="h-4 w-4 ml-2 text-neutral-400" />
          </label>
        )}

        {/* Search Bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search states or cities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={cn(
              "w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            disabled={disabled}
          />
        </div>

        {/* Selection Summary */}
        {selectedStateCount > 0 && (
          <div className="text-sm text-primary-600 font-medium">
            {selectedStateCount} state{selectedStateCount !== 1 ? 's' : ''} selected
            {showCities && selectedCityCount > 0 && (
              <span className="text-neutral-600 font-normal">
                {' • '}{selectedCityCount} cit{selectedCityCount !== 1 ? 'ies' : 'y'}
              </span>
            )}
          </div>
        )}

        {/* States Container */}
        <div className={cn(
          "border border-neutral-300 rounded-md bg-white max-h-96 overflow-y-auto",
          error && "border-red-500",
          disabled && "opacity-50 cursor-not-allowed"
        )}>
          {filteredStates.length === 0 ? (
            <div className="p-4 text-center text-neutral-500">
              No states or cities found matching "{searchTerm}"
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {filteredStates.map((state) => {
                const isExpanded = expandedStates.has(state.id)
                const isFullySelected = isStateFullySelected(state)
                const isPartiallySelected = isStatePartiallySelected(state)
                const isSelected = value.states.includes(state.id)

                return (
                  <div key={state.id} className="p-0">
                    {/* State Row */}
                    <div 
                      className={cn(
                        "flex items-center p-3 hover:bg-neutral-50 cursor-pointer transition-colors",
                        isSelected && "bg-primary-50",
                        disabled && "cursor-not-allowed hover:bg-transparent"
                      )}
                      onClick={() => {
                        if (!disabled) {
                          toggleState(state.id)
                          if (showCities) {
                            toggleStateExpansion(state.id)
                          }
                        }
                      }}
                    >
                      <div className="flex items-center flex-1">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Handled by parent onClick
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                            disabled={disabled}
                          />
                          {isFullySelected && (
                            <CheckIcon className="h-3 w-3 text-white absolute top-0.5 left-0.5 pointer-events-none" />
                          )}
                          {isPartiallySelected && !isFullySelected && (
                            <div className="h-3 w-3 bg-primary-600 absolute top-0.5 left-0.5 rounded-sm" />
                          )}
                        </div>
                        <span className={cn(
                          "ml-3 font-medium text-neutral-900",
                          isSelected && "text-primary-700"
                        )}>
                          {state.name}
                        </span>
                        {isPartiallySelected && !isFullySelected && (
                          <span className="ml-2 text-xs text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
                            {state.cities.filter(city => value.cities.includes(city)).length} cities
                          </span>
                        )}
                      </div>
                      {showCities && (
                        <ChevronRightIcon 
                          className={cn(
                            "h-4 w-4 text-neutral-400 transition-transform",
                            isExpanded && "rotate-90"
                          )} 
                        />
                      )}
                    </div>

                    {/* Cities Section */}
                    {showCities && isSelected && isExpanded && (
                      <div className="bg-neutral-50 px-3 py-2 border-t border-neutral-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {state.cities.map((city) => {
                            const isCitySelected = value.cities.includes(city)
                            return (
                              <label
                                key={city}
                                className={cn(
                                  "flex items-center p-2 rounded-md cursor-pointer transition-colors",
                                  isCitySelected ? "bg-white border border-primary-200" : "hover:bg-white"
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={isCitySelected}
                                  onChange={(e) => {
                                    e.stopPropagation()
                                    toggleCity(city, state.id)
                                  }}
                                  className="h-3 w-3 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                                  disabled={disabled}
                                />
                                <span className={cn(
                                  "ml-2 text-xs text-neutral-700",
                                  isCitySelected && "text-primary-700 font-medium"
                                )}>
                                  {city}
                                </span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
        {helperText && !error && (
          <p className="text-sm text-neutral-500">{helperText}</p>
        )}

        {maxSelections && (
          <p className="text-xs text-neutral-500">
            Select up to {maxSelections} state{maxSelections !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    )
  }
)

LocationSelector.displayName = 'LocationSelector'

export { LocationSelector }
